import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { FileItem, Folder, RecentlyViewedRecord } from '@/types'

interface UIState {
  // Navigation
  selectedFile: FileItem | null
  selectedFolder: Folder | null
  selectedFiles: number[] // Changed from Set to array for serializability
  lastSelectedFileId: number | null // For range selection with Shift+click

  // Recently viewed files with timestamps
  recentlyViewedRecords: RecentlyViewedRecord[]

  // Folder tree expansion
  expandedFolders: number[] // Changed from Set to array for serializability

  // Search and navigation history
  navigationHistory: {
    file: FileItem | null
    folder: Folder | null
  }

  // Modal states
  isCreateFolderModalOpen: boolean
  isEditFolderModalOpen: boolean
  isDeleteFolderModalOpen: boolean
  isUploadModalOpen: boolean

  // Edit/delete modal data
  folderToEdit: Folder | null
  folderToDelete: Folder | null
}

const initialState: UIState = {
  selectedFile: null,
  selectedFolder: null,
  selectedFiles: [],
  lastSelectedFileId: null,
  recentlyViewedRecords: [],
  expandedFolders: [0], // Root folder expanded by default
  navigationHistory: {
    file: null,
    folder: null,
  },
  isCreateFolderModalOpen: false,
  isEditFolderModalOpen: false,
  isDeleteFolderModalOpen: false,
  isUploadModalOpen: false,
  folderToEdit: null,
  folderToDelete: null,
}

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Navigation
    selectFile: (state, action: PayloadAction<FileItem | null>) => {
      state.selectedFile = action.payload

      // Add to recently viewed if file is selected
      if (action.payload) {
        const file = action.payload
        // Remove existing entry if present
        state.recentlyViewedRecords = state.recentlyViewedRecords.filter(
          (record) => record.file.id !== file.id
        )
        // Create new record with current timestamp
        const newRecord: RecentlyViewedRecord = {
          id: Date.now(), // Temporary ID for Redux state
          user_id: 0, // Will be set by backend
          file_id: file.id,
          organization_id: file.organization_id,
          viewed_at: new Date().toISOString(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          file: file,
        }
        // Add to beginning of array
        state.recentlyViewedRecords.unshift(newRecord)
        // Keep only last 10 records
        if (state.recentlyViewedRecords.length > 10) {
          state.recentlyViewedRecords = state.recentlyViewedRecords.slice(0, 10)
        }
      }
    },

    selectFolder: (state, action: PayloadAction<Folder | null>) => {
      state.selectedFolder = action.payload
      // Clear file selection when selecting a folder
      if (action.payload) {
        state.selectedFile = null
        state.selectedFiles = []
        state.lastSelectedFileId = null
      }
    },

    // Multi-file selection
    toggleFileSelection: (state, action: PayloadAction<number>) => {
      const fileId = action.payload
      const index = state.selectedFiles.indexOf(fileId)

      if (index > -1) {
        // Remove if already selected
        state.selectedFiles.splice(index, 1)
        // If we removed the last selected file, update lastSelectedFileId
        if (state.lastSelectedFileId === fileId) {
          state.lastSelectedFileId =
            state.selectedFiles.length > 0
              ? state.selectedFiles[state.selectedFiles.length - 1]
              : null
        }
      } else {
        // Add if not selected
        state.selectedFiles.push(fileId)
        // Update last selected file
        state.lastSelectedFileId = fileId
      }
    },

    selectAllFiles: (state, action: PayloadAction<number[]>) => {
      state.selectedFiles = action.payload
    },

    // Union rather than replace, so selecting a page of a paginated table keeps
    // whatever was already selected on the other pages.
    addFilesToSelection: (state, action: PayloadAction<number[]>) => {
      const fileIds = action.payload
      if (fileIds.length === 0) return

      const existing = new Set(state.selectedFiles)
      fileIds.forEach((fileId) => {
        if (!existing.has(fileId)) {
          existing.add(fileId)
          state.selectedFiles.push(fileId)
        }
      })

      state.lastSelectedFileId = fileIds[fileIds.length - 1]
    },

    removeFilesFromSelection: (state, action: PayloadAction<number[]>) => {
      const fileIds = action.payload
      if (fileIds.length === 0) return

      const removed = new Set(fileIds)
      state.selectedFiles = state.selectedFiles.filter((fileId) => !removed.has(fileId))

      if (state.lastSelectedFileId !== null && removed.has(state.lastSelectedFileId)) {
        state.lastSelectedFileId =
          state.selectedFiles.length > 0
            ? state.selectedFiles[state.selectedFiles.length - 1]
            : null
      }
    },

    clearFileSelection: (state) => {
      state.selectedFiles = []
      state.lastSelectedFileId = null
    },

    // Range selection with Shift+click
    selectFileRange: (
      state,
      action: PayloadAction<{ fileIds: number[]; lastSelectedId: number }>
    ) => {
      const { fileIds, lastSelectedId } = action.payload
      state.selectedFiles = fileIds
      state.lastSelectedFileId = lastSelectedId
      // Clear single selection since we have multiple
      state.selectedFile = null
    },

    // Single file selection (clears other selections)
    selectSingleFile: (state, action: PayloadAction<number>) => {
      const fileId = action.payload
      state.selectedFiles = [fileId]
      state.lastSelectedFileId = fileId
      state.selectedFile = null // Clear single file selection when multi-selecting
    },

    // Folder expansion
    toggleFolderExpansion: (state, action: PayloadAction<number>) => {
      const folderId = action.payload
      const index = state.expandedFolders.indexOf(folderId)

      if (index > -1) {
        // Remove if already expanded
        state.expandedFolders.splice(index, 1)
      } else {
        // Add if not expanded
        state.expandedFolders.push(folderId)
      }
    },

    setFolderExpanded: (state, action: PayloadAction<{ folderId: number; expanded: boolean }>) => {
      const { folderId, expanded } = action.payload
      const index = state.expandedFolders.indexOf(folderId)

      if (expanded && index === -1) {
        // Add if not already expanded
        state.expandedFolders.push(folderId)
      } else if (!expanded && index > -1) {
        // Remove if currently expanded
        state.expandedFolders.splice(index, 1)
      }
    },

    // Auto-expand folder path when selecting a folder
    expandFolderPath: (state, action: PayloadAction<{ folderId: number; parentIds: number[] }>) => {
      const { folderId, parentIds } = action.payload
      const idsToExpand = [folderId, ...parentIds, 0] // Include root (0)

      // Add each ID if not already in the array
      idsToExpand.forEach((id) => {
        if (!state.expandedFolders.includes(id)) {
          state.expandedFolders.push(id)
        }
      })
    },

    // Navigation history for search
    saveNavigationForSearch: (state) => {
      state.navigationHistory = {
        file: state.selectedFile,
        folder: state.selectedFolder,
      }
    },

    restoreNavigationFromSearch: (state) => {
      state.selectedFile = state.navigationHistory.file
      state.selectedFolder = state.navigationHistory.folder
    },

    // Modal management
    openCreateFolderModal: (state) => {
      state.isCreateFolderModalOpen = true
    },

    closeCreateFolderModal: (state) => {
      state.isCreateFolderModalOpen = false
    },

    openEditFolderModal: (state, action: PayloadAction<Folder>) => {
      state.isEditFolderModalOpen = true
      state.folderToEdit = action.payload
    },

    closeEditFolderModal: (state) => {
      state.isEditFolderModalOpen = false
      state.folderToEdit = null
    },

    openDeleteFolderModal: (state, action: PayloadAction<Folder>) => {
      state.isDeleteFolderModalOpen = true
      state.folderToDelete = action.payload
    },

    closeDeleteFolderModal: (state) => {
      state.isDeleteFolderModalOpen = false
      state.folderToDelete = null
    },

    openUploadModal: (state) => {
      state.isUploadModalOpen = true
    },

    closeUploadModal: (state) => {
      state.isUploadModalOpen = false
    },

    // Navigation helpers
    navigateToParent: (state, action: PayloadAction<Folder | null>) => {
      state.selectedFolder = action.payload
      state.selectedFile = null
      state.selectedFiles = []
    },

    navigateToRoot: (state) => {
      state.selectedFolder = null
      state.selectedFile = null
      state.selectedFiles = []
    },

    // Recently viewed management
    initializeRecentlyViewed: (state, action: PayloadAction<RecentlyViewedRecord[]>) => {
      state.recentlyViewedRecords = action.payload.slice(0, 10) // Limit to 10 records
    },

    addToRecentlyViewed: (state, action: PayloadAction<FileItem>) => {
      const file = action.payload
      // Remove existing entry if present
      state.recentlyViewedRecords = state.recentlyViewedRecords.filter(
        (record) => record.file.id !== file.id
      )
      // Create new record with current timestamp
      const newRecord: RecentlyViewedRecord = {
        id: Date.now(), // Temporary ID for Redux state
        user_id: 0, // Will be set by backend
        file_id: file.id,
        organization_id: file.organization_id,
        viewed_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        file: file,
      }
      // Add to beginning of array
      state.recentlyViewedRecords.unshift(newRecord)
      // Keep only last 10 records
      if (state.recentlyViewedRecords.length > 10) {
        state.recentlyViewedRecords = state.recentlyViewedRecords.slice(0, 10)
      }
    },

    removeFromRecentlyViewed: (state, action: PayloadAction<number>) => {
      const fileId = action.payload
      state.recentlyViewedRecords = state.recentlyViewedRecords.filter(
        (record) => record.file.id !== fileId
      )
    },

    clearRecentlyViewed: (state) => {
      state.recentlyViewedRecords = []
    },

    // Clear all UI state (for logout)
    clearUIState: (state) => {
      return { ...initialState }
    },
  },
})

export const {
  selectFile,
  selectFolder,
  toggleFileSelection,
  selectAllFiles,
  addFilesToSelection,
  removeFilesFromSelection,
  clearFileSelection,
  selectFileRange,
  selectSingleFile,
  toggleFolderExpansion,
  setFolderExpanded,
  expandFolderPath,
  saveNavigationForSearch,
  restoreNavigationFromSearch,
  openCreateFolderModal,
  closeCreateFolderModal,
  openEditFolderModal,
  closeEditFolderModal,
  openDeleteFolderModal,
  closeDeleteFolderModal,
  openUploadModal,
  closeUploadModal,
  navigateToParent,
  navigateToRoot,
  initializeRecentlyViewed,
  addToRecentlyViewed,
  removeFromRecentlyViewed,
  clearRecentlyViewed,
  clearUIState,
} = uiSlice.actions

export default uiSlice.reducer
